import { MatchUploadQueueDialog } from "@/features/matches/components/MatchUploadQueueDialog";
import "../match-library.css";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import {
  ApiError,
  apiFetch,
  getAdminTeams,
  getAdminMatchLibrary,
} from "@/lib/api";
import { MatchCollectionFooter } from "@/features/matches/components/MatchCollectionFooter";
import { MatchLibraryRow } from "../components/MatchLibraryRow";
import {
  LibraryDialog,
  MatchDetailsDialog,
  TransferMatchDialog,
} from "../components/MatchLibraryDialogs";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  DataTable,
  DataTableBody,
  DataTableEmptyRow,
  DataTableHead,
  DataTableHeadCell,
  DataTableRow,
  DataTableScroll,
  DataTableShell,
} from "@/components/ui/DataTable";
import { unscopedKey, useViewerHostKey } from "@/lib/queryClient";
import type { Match, Season, AdminTeamRow, MeTeam } from "@/lib/types";
import { useMe } from "@/features/auth/useMe";
import { useAuthStore } from "@/stores/auth";
import { MatchEditModal } from "@/features/matches/components/MatchEditModal";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useUploadQueueContext } from "@/features/coach/components/UploadQueueContext";
import type { UploadSlot } from "@/features/coach/components/useUploadQueue";
import { useTranscodeProgress } from "../hooks/useAdminData";
import { AdminPageHeader } from "@/components/console/AdminPageHeader";
import { MatchSlotPanel } from "./MatchSlotPanel";
import { knownTournamentNames } from "@/features/matches/matchCompetition";

/** Club-scoped server-paginated match library. Operational actions remain
 * independent of row details; ownership changes use the explicit transfer API. */
/** Stable empty-array identity so the `me.seasons` selector doesn't
 * return a fresh `[]` each render (which would re-fire `useMemo`). */
const EMPTY_SEASONS: Season[] = [];

type MatchTeamOption = Pick<AdminTeamRow | MeTeam, "id" | "name">;


function MatchFilterField({
  label,
  className = "",
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={`admin-match-filter-field ${className}`.trim()}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function normalizeTeamOptions<T extends MatchTeamOption>(
  teams: T[],
): Array<{ id: string; name: string }> {
  const byId = new Map<string, { id: string; name: string }>();
  for (const team of teams) {
    byId.set(team.id, { id: team.id, name: team.name });
  }
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export function AdminMatchesPage() {
  const me = useMe().data;
  const isGlobalAdmin = !!me?.user?.is_global_admin;
  // A pure club_admin has NO team_user_memberships row, so `me.teams` is empty.
  // Load the host club's manageable teams independently of active team scope.
  const isClubAdmin = !!me?.is_club_admin;
  const meTeams = me?.teams ?? [];

  // B2 — filters.
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "none" | "ready" | "transcoding" | "error" | "partial"
  >("all");
  const [matchStatusFilter, setMatchStatusFilter] = useState<
    "all" | "scheduled" | "completed" | "postponed" | "cancelled"
  >("all");
  const [competitionFilter, setCompetitionFilter] = useState<
    "all" | "league" | "tournament" | "friendly"
  >("all");
  const [formatFilter, setFormatFilter] = useState<"all" | "full" | "separate">(
    "all",
  );
  const [teamFilter, setTeamFilter] = useState<string>("all");
  const [seasonFilter, setSeasonFilter] = useState<string>("all");
  const [sort, setSort] = useState<"date_asc" | "date_desc" | "updated_desc">(
    "date_desc",
  );
  const [pageSize, setPageSize] = useState("10");
  const [page, setPage] = useState(1);

  const adminTeamsQuery = useQuery({
    queryKey: unscopedKey("admin-teams"),
    queryFn: getAdminTeams,
    enabled: isGlobalAdmin || isClubAdmin,
    staleTime: 60_000,
  });
  const matchTeamOptions = useMemo(
    () =>
      isGlobalAdmin || isClubAdmin
        ? normalizeTeamOptions(adminTeamsQuery.data ?? [])
        : normalizeTeamOptions(
            meTeams.filter((team) => team.capabilities.includes("match:write")),
          ),
    [adminTeamsQuery.data, isGlobalAdmin, isClubAdmin, meTeams],
  );
  const matchesQuery = useQuery({
    queryKey: useViewerHostKey("admin-matches", {
      search,
      teamFilter,
      seasonFilter,
      competitionFilter,
      matchStatusFilter,
      statusFilter,
      formatFilter,
      sort,
      page,
      pageSize,
    }),
    queryFn: () =>
      getAdminMatchLibrary({
        q: search,
        team_id: teamFilter === "all" ? undefined : teamFilter,
        season_id: seasonFilter === "all" ? undefined : seasonFilter,
        competition: competitionFilter,
        match_status: matchStatusFilter,
        recording: statusFilter,
        video_format: formatFilter,
        sort,
        page,
        limit: Number(pageSize),
      }),
    enabled: !!me?.user,
    placeholderData: keepPreviousData,
  });
  const matches = matchesQuery.data?.matches ?? [];
  const filteredTotal = matchesQuery.data?.total ?? 0;
  const unfilteredTotal = matchesQuery.data?.unfiltered_total ?? 0;
  const teamNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of matchTeamOptions) map.set(t.id, t.name);
    return map;
  }, [matchTeamOptions]);

  // Gate transcode-progress polling on "any visible slot transcoding".
  const anyTranscoding = useMemo(() => {
    for (const m of matches) {
      for (const status of Object.values(m.video_status ?? {})) {
        if (status === "transcoding") return true;
      }
    }
    return false;
  }, [matches]);
  useTranscodeProgress({ enabled: anyTranscoding });

  // Season options come from the globally-shared `me.seasons` list —
  // the same source `SeasonLabel` resolves names from, so no extra
  // fetch. The default season is marked so the operator can tell which
  // one new matches inherit.
  const seasons = useAuthStore((s) => s.me?.seasons ?? EMPTY_SEASONS);
  const seasonOptions = useMemo(
    () => [
      { value: "all", label: "All seasons" },
      ...seasons.map((s) => ({
        value: s.id,
        label: s.is_default ? `${s.name} (default)` : s.name,
      })),
    ],
    [seasons],
  );

  const [detailsTarget, setDetailsTarget] = useState<Match | null>(null);
  const [moveTarget, setMoveTarget] = useState<Match | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<Match | "new" | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Match | null>(null);
  // Sequential multi-upload queue: per-row "Upload video" adds an item.
  // The queue lives in `UploadQueueProvider` at AdminShell level so it
  // (and the docked panel) survives navigation between admin sections.
  const uploadQueue = useUploadQueueContext();
  const [queueTarget, setQueueTarget] = useState<{
    match: Match;
    slot?: UploadSlot;
  } | null>(null);

  const collectionControls = unfilteredTotal > 5;
  useEffect(() => {
    setPage(1);
  }, [
    search,
    statusFilter,
    matchStatusFilter,
    competitionFilter,
    formatFilter,
    teamFilter,
    seasonFilter,
    sort,
    pageSize,
  ]);

  const pageSizeNumber = Number(pageSize);
  const totalPages = Math.max(1, Math.ceil(filteredTotal / pageSizeNumber));
  const safePage = matchesQuery.data?.page ?? Math.min(page, totalPages);
  const pageRows = matches;
  const firstVisible =
    filteredTotal === 0 ? 0 : (safePage - 1) * pageSizeNumber + 1;
  const lastVisible = Math.min(filteredTotal, safePage * pageSizeNumber);
  useEffect(() => {
    if (
      matchesQuery.data &&
      !matchesQuery.isPlaceholderData &&
      matchesQuery.data.page !== page
    )
      setPage(matchesQuery.data.page);
  }, [matchesQuery.data, matchesQuery.isPlaceholderData, page]);

  const teamFilterOptions = useMemo(
    () => [
      { value: "all", label: "All teams" },
      ...matchTeamOptions.map((t) => ({ value: t.id, label: t.name })),
    ],
    [matchTeamOptions],
  );
  useEffect(() => {
    if (teamFilter === "all") return;
    if (!teamFilterOptions.some((option) => option.value === teamFilter)) {
      setTeamFilter("all");
    }
  }, [teamFilter, teamFilterOptions]);

  const hasActiveFilters =
    search.trim() !== "" ||
    statusFilter !== "all" ||
    matchStatusFilter !== "all" ||
    competitionFilter !== "all" ||
    formatFilter !== "all" ||
    teamFilter !== "all" ||
    seasonFilter !== "all" ||
    sort !== "date_desc";

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setMatchStatusFilter("all");
    setCompetitionFilter("all");
    setFormatFilter("all");
    setTeamFilter("all");
    setSeasonFilter("all");
    setSort("date_desc");
    setPage(1);
  };

  return (
    <div data-testid="admin-matches" className="flex flex-col gap-3">
      <AdminPageHeader
        eyebrow="Media"
        title="Match Library"
        description="The club’s match film, ready for the right people."
        actions={
          <button
            type="button"
            onClick={() => setEditTarget("new")}
            disabled={matchTeamOptions.length === 0}
            data-testid="admin-matches-add"
            aria-label="Add match"
            title={
              matchTeamOptions.length === 0
                ? "No manageable teams available"
                : "Add match"
            }
            className="inline-flex h-8 w-8 items-center justify-center rounded border border-accent bg-accent text-accent-ink transition-colors hover:bg-accent-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus size={16} strokeWidth={2} aria-hidden="true" />
            <span className="admin-new-match-label">New match</span>
          </button>
        }
      />

      {matchesQuery.error ? (
        <p
          data-testid="admin-matches-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load matches.
        </p>
      ) : null}

      <section className="admin-match-library-pane admin-library-pane">
        {collectionControls ? (
          <div
            className="admin-match-collection-toolbar"
            role="group"
            aria-label="Search and filter matches"
          >
            <label className="admin-match-search-field">
              <span>Quick search</span>
              <span className="admin-match-search-control">
                <Search size={16} aria-hidden="true" />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Team, tournament, stage or date"
                  data-testid="admin-matches-search"
                />
              </span>
            </label>
            <MatchFilterField label="Team">
              <TkticSelect
                value={teamFilter}
                onChange={setTeamFilter}
                data-testid="admin-matches-team-filter"
                size="sm"
                ariaLabel="Team"
                options={teamFilterOptions.map((option) => ({
                  ...option,
                  label: option.value === "all" ? "All" : option.label,
                }))}
              />
            </MatchFilterField>
            <MatchFilterField label="Competition">
              <TkticSelect
                value={competitionFilter}
                onChange={(next) =>
                  setCompetitionFilter(next as typeof competitionFilter)
                }
                data-testid="admin-matches-competition-filter"
                size="sm"
                ariaLabel="Competition"
                options={[
                  { value: "all", label: "All" },
                  { value: "league", label: "League" },
                  { value: "tournament", label: "Tournament" },
                  { value: "friendly", label: "Friendly" },
                ]}
              />
            </MatchFilterField>
            <MatchFilterField label="Match status">
              <TkticSelect
                value={matchStatusFilter}
                onChange={(next) =>
                  setMatchStatusFilter(next as typeof matchStatusFilter)
                }
                data-testid="admin-matches-match-status-filter"
                size="sm"
                ariaLabel="Match status"
                options={[
                  { value: "all", label: "All" },
                  { value: "scheduled", label: "Scheduled" },
                  { value: "completed", label: "Completed" },
                  { value: "postponed", label: "Postponed" },
                  { value: "cancelled", label: "Cancelled" },
                ]}
              />
            </MatchFilterField>
            <MatchFilterField label="Recording">
              <TkticSelect
                value={statusFilter}
                onChange={(next) =>
                  setStatusFilter(next as typeof statusFilter)
                }
                data-testid="admin-matches-status-filter"
                size="sm"
                ariaLabel="Recording"
                options={[
                  { value: "all", label: "All" },
                  { value: "ready", label: "Ready" },
                  { value: "transcoding", label: "Processing" },
                  { value: "error", label: "Error" },
                  { value: "partial", label: "Partial" },
                  { value: "none", label: "Unavailable" },
                ]}
              />
            </MatchFilterField>
            <MatchFilterField label="Season">
              <TkticSelect
                value={seasonFilter}
                onChange={setSeasonFilter}
                data-testid="admin-matches-season-filter"
                size="sm"
                ariaLabel="Season"
                options={seasonOptions.map((option) => ({
                  ...option,
                  label: option.value === "all" ? "All" : option.label,
                }))}
              />
            </MatchFilterField>
            <MatchFilterField label="Video format">
              <TkticSelect
                value={formatFilter}
                onChange={(next) =>
                  setFormatFilter(next as typeof formatFilter)
                }
                data-testid="admin-matches-format-filter"
                size="sm"
                ariaLabel="Video format"
                options={[
                  { value: "all", label: "All" },
                  { value: "full", label: "Full match" },
                  { value: "separate", label: "Separate recordings" },
                ]}
              />
            </MatchFilterField>
            <button
              type="button"
              className="admin-match-clear-filters"
              disabled={!hasActiveFilters}
              onClick={clearFilters}
            >
              Clear filters
            </button>
          </div>
        ) : null}

        <DataTableShell className="admin-library-table-shell">
          <DataTableScroll>
            <DataTable
              data-testid="admin-matches-table"
              className="admin-library-table"
            >
              <colgroup>
                {[27, 13, 14, 10, 15, 21].map((width, index) => (
                  <col key={index} style={{ width: `${width}%` }} />
                ))}
              </colgroup>
              <DataTableHead>
                <DataTableRow interactive={false}>
                  {[
                    "Match",
                    "Date & season",
                    "Competition",
                    "Result",
                    "Recording",
                    "Actions",
                  ].map((label) => (
                    <DataTableHeadCell key={label}>{label}</DataTableHeadCell>
                  ))}
                </DataTableRow>
              </DataTableHead>
              <DataTableBody>
                {matchesQuery.isLoading ? (
                  <DataTableEmptyRow colSpan={6}>
                    Loading matches…
                  </DataTableEmptyRow>
                ) : matchesQuery.isError ? (
                  <DataTableEmptyRow colSpan={6}>
                    Could not load matches.{" "}
                    <button
                      type="button"
                      onClick={() => matchesQuery.refetch()}
                    >
                      Try again
                    </button>
                  </DataTableEmptyRow>
                ) : !filteredTotal ? (
                  <DataTableEmptyRow colSpan={6}>
                    {unfilteredTotal
                      ? "No matching results. Clear filters to see all matches."
                      : "No matches yet. Create the club’s first match."}
                  </DataTableEmptyRow>
                ) : null}
                {pageRows.map((match) => (
                  <MatchLibraryRow
                    key={match.id}
                    match={match}
                    teamName={
                      teamNameById.get(match.team_id ?? "") ?? "No team"
                    }
                    seasonName={
                      seasons.find((s) => s.id === match.season_id)?.name ??
                      "No season"
                    }
                    onDetails={() => setDetailsTarget(match)}
                    onMedia={() => setExpanded(match.id)}
                    onEdit={() => setEditTarget(match)}
                    onDelete={() => setDeleteTarget(match)}
                    onUpload={() => setQueueTarget({ match })}
                    onMove={
                      isClubAdmin || me?.is_super_admin
                        ? () => setMoveTarget(match)
                        : undefined
                    }
                  />
                ))}
              </DataTableBody>
            </DataTable>
          </DataTableScroll>
        </DataTableShell>

        {collectionControls ? (
          <MatchCollectionFooter
            first={firstVisible}
            last={lastVisible}
            total={filteredTotal}
            page={safePage}
            totalPages={totalPages}
            onPageChange={setPage}
            sort={sort}
            onSortChange={setSort}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            testIdPrefix="admin-matches"
          />
        ) : null}
      </section>

      {detailsTarget ? (
        <MatchDetailsDialog
          match={detailsTarget}
          teamName={teamNameById.get(detailsTarget.team_id ?? "") ?? "No team"}
          seasonName={
            seasons.find((s) => s.id === detailsTarget.season_id)?.name ??
            "No season"
          }
          onClose={() => setDetailsTarget(null)}
          onMedia={() => {
            setExpanded(detailsTarget.id);
            setDetailsTarget(null);
          }}
          onEdit={() => {
            setEditTarget(detailsTarget);
            setDetailsTarget(null);
          }}
        />
      ) : null}
      {moveTarget ? (
        <TransferMatchDialog
          match={moveTarget}
          teams={matchTeamOptions}
          seasons={seasons}
          onClose={() => setMoveTarget(null)}
        />
      ) : null}
      {expanded ? (
        <MatchRecordingsDialog
          match={matches.find((match) => match.id === expanded) ?? null}
          teamName={(() => {
            const match = matches.find(
              (candidate) => candidate.id === expanded,
            );
            return match?.team_id
              ? (teamNameById.get(match.team_id) ?? match.team_id)
              : null;
          })()}
          onClose={() => setExpanded(null)}
          onUpload={(slot) => {
            const match = matches.find(
              (candidate) => candidate.id === expanded,
            );
            if (match) {
              setExpanded(null);
              setQueueTarget({ match, slot });
            }
          }}
        />
      ) : null}

      {/* B6 — add/edit match form (M12b: extracted into shared
       * `MatchEditModal` so the coach Matches tab + admin Matches page
       * share the same modal). */}
      {editTarget ? (
        <MatchEditModal
          match={editTarget === "new" ? null : editTarget}
          teamOptions={matchTeamOptions}
          knownTournaments={knownTournamentNames(matches)}
          onClose={() => setEditTarget(null)}
        />
      ) : null}

      {/* B7 — delete confirm */}
      {deleteTarget ? (
        <DeleteMatchConfirm
          match={deleteTarget}
          onClose={() => setDeleteTarget(null)}
        />
      ) : null}

      {/* Per-row "Upload video" → slot + file picker → adds to the queue.
       * The docked `UploadQueuePanel` is rendered by `AdminShell`, not
       * here, so it persists across admin route changes. */}
      {queueTarget ? (
        <MatchUploadQueueDialog
          match={queueTarget.match}
          initialSlot={queueTarget.slot}
          onAdd={(slot, file) => {
            uploadQueue.enqueue(queueTarget.match, slot, file);
            setQueueTarget(null);
          }}
          onClose={() => setQueueTarget(null)}
        />
      ) : null}
    </div>
  );
}

function MatchRecordingsDialog({
  match,
  teamName,
  onClose,
  onUpload,
}: {
  match: Match | null;
  teamName: string | null;
  onClose: () => void;
  onUpload: (slot: UploadSlot) => void;
}) {
  if (!match) return null;
  return (
    <LibraryDialog
      matchId={match.id}
      width={deriveFormatGroup(match) === "full" ? "standard" : "recordings"}
      title="Match recordings"
      onClose={onClose}
      testId="admin-matches-row-diagnostics"
      description={`${teamName ?? "No team"} · ${match.home_team} vs ${match.away_team} · ${recordingLayoutLabel(match)}`}
    >
      <div
        className="admin-library-dialog-body"
        data-recording-layout={deriveFormatGroup(match)}
      >
        <MatchSlotPanel match={match} onUpload={onUpload} />
        <p className="admin-match-recordings-note">
          Recording uploads join the club upload queue. Extra time and shootout
          are independent optional recordings.
        </p>
      </div>
      <footer>
        <button type="button" onClick={onClose}>
          Close
        </button>
      </footer>
    </LibraryDialog>
  );
}

function recordingLayoutLabel(match: Match): string {
  return deriveFormatGroup(match) === "separate"
    ? "Separate recordings"
    : "Full match";
}

async function invalidateAdminMatches(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: unscopedKey("public-matches") }),
    queryClient.invalidateQueries({ queryKey: unscopedKey("admin-matches") }),
    queryClient.invalidateQueries({
      queryKey: unscopedKey("admin-matches-allowed"),
    }),
    queryClient.invalidateQueries({ queryKey: ["admin-matches-scoped"] }),
    queryClient.invalidateQueries({ queryKey: ["coach-matches"] }),
    // The club Overview aggregates match/slot state (admin-console-split).
    queryClient.invalidateQueries({ queryKey: unscopedKey("club-overview") }),
  ]);
}

function DeleteMatchConfirm({
  match,
  onClose,
}: {
  match: Match;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await apiFetch(`/api/matches/${encodeURIComponent(match.id)}`, {
        method: "DELETE",
        scope: false,
      });
      await invalidateAdminMatches(queryClient);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "Failed to delete match.",
        );
      } else {
        setError("Failed to delete match.");
      }
      setSubmitting(false);
    }
  };

  return (
    <ConfirmDialog
      title="Delete match?"
      message={
        <>
          <p>
            <strong>
              {match.home_team || "—"} vs {match.away_team || "—"}
            </strong>{" "}
            ({match.date || "no date"}) will be permanently removed along with
            all recordings and linked coaching notes, clips, and recaps.
          </p>
          {error ? (
            <p
              data-testid="admin-matches-delete-error"
              className="mt-2 text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </>
      }
      confirmLabel={submitting ? "Deleting…" : "Delete match"}
      danger
      onConfirm={onConfirm}
      onClose={onClose}
      data-testid="admin-matches-delete-confirm"
    />
  );
}

/** Aggregate a match's per-slot statuses into a single bucket the
 * filter dropdown can use. */

function deriveFormatGroup(match: Match): "full" | "separate" | "other" {
  if (match.format === "full") return "full";
  if (match.format === "two_halves" || match.format === "separate")
    return "separate";
  return "other";
}
